import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { ShieldAlert, ShieldCheck, AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * ProductionHealth — Operations card surfacing the same data as
 * /api/lab/health, with explicit alert rules and an external-monitor
 * setup link. Designed to be the single pane for production status.
 */
export default function ProductionHealth() {
  const [health, setHealth] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      api.get('/lab/health')
        .then(({ data }) => { if (!cancelled) setHealth(data); })
        .catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [tick]);

  if (!health) return <div className="p-4 text-[11px] text-[var(--txt-secondary)]">loading health…</div>;

  const s = health.status || {};
  const alerts = health.alerts || [];
  const isOk = alerts.length === 0 && s.status === 'ok';

  return (
    <div data-testid="lab-prod-health" className="space-y-3">
      {/* MASTER STATE */}
      <div className={`border p-3 flex items-center justify-between ${isOk ? 'border-buy' : 'border-hold'}`}>
        <div className="flex items-center gap-3">
          {isOk
            ? <ShieldCheck size={28} className="text-buy" />
            : <ShieldAlert size={28} className="text-hold" />}
          <div>
            <div className={`text-[14px] tracking-[0.3em] uppercase ${isOk ? 'text-buy' : 'text-hold'}`}>
              {isOk ? 'ALL SYSTEMS NOMINAL' : 'ATTENTION REQUIRED'}
            </div>
            <div className="text-[10px] text-[var(--txt-secondary)]">
              env={s.environment} · checked {(health.checked_at || '').slice(11, 19)}Z
            </div>
          </div>
        </div>
        <button
          data-testid="lab-prod-health-refresh"
          onClick={() => setTick((x) => x + 1)}
          className="border border-cyan-sys text-cyan-sys px-2 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-cyan-sys hover:text-black flex items-center gap-1"
        >
          <RefreshCw size={11} /> refresh
        </button>
      </div>

      {/* ALERTS */}
      {alerts.length > 0 && (
        <div data-testid="lab-prod-health-alerts" className="space-y-1">
          {alerts.map((a, i) => (
            <div
              key={i}
              data-testid={`lab-prod-alert-${a.severity}`}
              className={`border p-2 text-[11px] flex items-center gap-2 ${
                a.severity === 'critical' ? 'border-sell text-sell' : 'border-hold text-hold'
              }`}
            >
              <AlertTriangle size={13} /> [{a.severity}] {a.msg}
            </div>
          ))}
        </div>
      )}

      {/* DETAIL TILES */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono">
        <Tile k="API"     v={s.status} ok={s.status === 'ok'} />
        <Tile k="MongoDB" v={s.mongo?.connected ? `${s.mongo.latency_ms}ms` : 'down'} ok={s.mongo?.connected} />
        <Tile k="DB Name" v={s.mongo?.db} />
        <Tile k="Uptime"  v={s.uptime_seconds != null ? `${(s.uptime_seconds / 3600).toFixed(1)}h` : '—'} />
        <Tile k="Env"     v={s.environment} />
        <Tile k="Version" v={(s.version || 'dev').slice(0, 7)} />
        <Tile k="Built"   v={short(s.build_time)} />
        <Tile k="Booted"  v={short(s.boot_time)} />
      </div>

      {/* SSL */}
      {s.ssl ? (
        <div className="border border-[var(--border-default)] p-3 text-[11px] font-mono">
          <div className="text-[10px] tracking-[0.25em] uppercase text-cyan-sys mb-2">// SSL / TLS</div>
          <Row k="domain"  v={s.ssl.domain} />
          <Row k="expires" v={short(s.ssl.not_after)} />
          <Row
            k="days left"
            v={s.ssl.days_until_expiry}
            color={s.ssl.ok ? 'text-buy' : 'text-sell'}
          />
        </div>
      ) : (
        <div className="border border-[var(--border-default)] p-3 text-[11px] text-[var(--txt-secondary)]">
          SSL info only populates in production (when PUBLIC_DOMAIN is set).
        </div>
      )}

      {/* LAST DEPLOY */}
      <div className="border border-[var(--border-default)] p-3 text-[11px] font-mono">
        <div className="text-[10px] tracking-[0.25em] uppercase text-cyan-sys mb-2">// last deployment</div>
        {health.last_deploy ? (
          <>
            <Row k="sha"      v={health.last_deploy.sha} />
            <Row k="status"   v={health.last_deploy.status} color="text-cyan-sys" />
            <Row k="deployed" v={short(health.last_deploy.deployed_at)} />
            <Row k="by"       v={health.last_deploy.author} />
            <Row k="notes"    v={health.last_deploy.notes || '—'} />
          </>
        ) : (
          <div className="text-[var(--txt-secondary)]">No deployment records yet.</div>
        )}
      </div>

      {/* EXTERNAL MONITOR */}
      <div className="border border-[var(--border-default)] p-3">
        <div className="text-[10px] tracking-[0.25em] uppercase text-cyan-sys mb-2">// external uptime monitor</div>
        <p className="text-[11px] text-[var(--txt-secondary)] mb-2">
          Wire UptimeRobot or Better Stack to these endpoints — see
          <code className="mx-1 text-cyan-sys">deploy/MONITORING.md</code> for the full
          setup. Recommended alert rules:
        </p>
        <ul className="text-[11px] font-mono space-y-0.5">
          <li>· <span className="text-cyan-sys">https://atdecisionhub.net/api/health/live</span> — heartbeat (1 min)</li>
          <li>· <span className="text-cyan-sys">https://atdecisionhub.net/api/status</span> — detailed health (5 min)</li>
          <li>· Alert: API unreachable, mongo.connected=false, status≠ok, SSL days_until_expiry &lt; 14</li>
        </ul>
      </div>
    </div>
  );
}

function Tile({ k, v, ok }) {
  const color = ok === true ? 'text-buy' : ok === false ? 'text-sell' : 'text-[var(--txt-primary)]';
  return (
    <div className="border border-[var(--border-default)] p-2">
      <div className="text-[9px] tracking-[0.25em] uppercase text-[var(--txt-secondary)]">{k}</div>
      <div className={`text-[14px] tabular ${color}`}>{String(v ?? '—')}</div>
    </div>
  );
}

function Row({ k, v, color }) {
  return (
    <div className="flex justify-between gap-3 leading-5">
      <span className="text-[var(--txt-secondary)] uppercase tracking-wider text-[10px]">{k}</span>
      <span className={`tabular ${color || 'text-[var(--txt-primary)]'}`}>{String(v ?? '—')}</span>
    </div>
  );
}

function short(iso) {
  if (!iso) return '—';
  return String(iso).replace('T', ' ').slice(0, 19) + 'Z';
}
